<template>
  <div class="range-slider min-h-[70px]" data-testid="range-slider">
    <div class="mb-4">
      <span
        v-if="modelValue[0] || modelValue[0] === 0"
        class="whitespace-nowrap text-lg"
      >
        {{ valueFormatter ? valueFormatter(modelValue[0]) : modelValue[0] }}
      </span>
      <span v-if="modelValue[1]" class="whitespace-nowrap text-lg">
        {{ ' – ' }}
        {{ valueFormatter ? valueFormatter(modelValue[1]) : modelValue[1] }}
      </span>
    </div>
    <VueSlider
      ref="sliderRef"
      v-model="range"
      :enable-cross="false"
      :contained="true"
      adsorb
      tooltip="none"
      :min="min"
      :max="max"
      :min-range="1000"
      :rail-style="{
        background: '#F5F7FA',
        height: '4px',
        border: '1px solid #D7D7D9',
      }"
      :tooltip-formatter="valueFormatter"
      data-testid="range-slider-slider"
      @change="emit('change')"
      @drag-start="emit('drag-start')"
      @drag-end="emit('drag-end')"
      @dragging="emit('dragging')"
      @error="emit('error')"
    >
      <template #dot>
        <button
          aria-hidden="true"
          class="size-6 shrink-0 cursor-pointer self-center rounded-full border bg-white shadow focus:outline-none"
        />
      </template>
    </VueSlider>
  </div>
</template>

<script setup lang="ts">
// @ts-expect-error no typescript definitions
import VueSlider from 'vue-slider-component/dist-css/vue-slider-component.umd.min.js'
import 'vue-slider-component/dist-css/vue-slider-component.css'

type RangeTuple = [number, number]

interface Props {
  modelValue: RangeTuple
  min?: number
  max?: number
  valueFormatter: (price: number) => string
  minLabel: string
  maxLabel: string
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [3, 29999],
  min: 0,
  max: 100000,
  minLabel: '',
  maxLabel: '',
})

const emit = defineEmits<{
  (e: 'update:model-value', value: RangeTuple): void
  (e: 'change' | 'drag-start' | 'drag-end' | 'dragging' | 'error'): void
}>()

const range = computed({
  get: (): RangeTuple => props.modelValue,
  set: (newValue: RangeTuple) => emit('update:model-value', newValue),
})

onMounted(() => {
  nextTick(() => {
    applyAriaLabels()
  })
})

watch(range, () => {
  nextTick(() => {
    applyAriaLabels()
  })
})

const sliderRef = ref<InstanceType<typeof VueSlider> | null>(null)

const applyAriaLabels = () => {
  const dotElements = sliderRef.value?.$el?.querySelectorAll('.vue-slider-dot')

  dotElements?.forEach((dot: Element, index: number) => {
    dot.setAttribute(
      'aria-label',
      index === 0 ? props.minLabel : props.maxLabel,
    )
  })
}
</script>

<style lang="scss">
$themeColor: #dcdee0;

.range-slider .vue-slider-dot {
  display: flex;
}
</style>
